import { FilterSectionListSelector } from "@components/Filter/FilterSectionListSelector";
import { Link } from "@components/Link/Link";
import { styledWithInvalidProps } from "@components/Styled/StyledWithInvalidProps";
import { css } from "@emotion/react";
import { Filter } from "@usereactify/search";
import React from "react";
import tw from "twin.macro";
import { withCollectionHeroBannerSizeFilters } from "./withCollectionHeroBannerSizeFilters";
import { Button, CustomImage } from "@bared/ui";
import clsx from "clsx";

const RteWrapper = styledWithInvalidProps("div")(({ contentAlignment }) => [
  `> .rte {
    display: flex;
    flex-direction: column;
    align-items: ${contentAlignment === "left" ? "flex-start" : "center"}
  }`,
]);

const SizeWrapper = styledWithInvalidProps("div")(
  ({ bgColor, contentAlignment, hoverBgColor, hoverTextColor, textColor }) => [
    tw`flex flex-wrap`,
    css`
      & ul {
        ${tw`py-micro`}
        ${"left" === contentAlignment ? tw`justify-start` : tw`justify-center`}
      }
      & li {
        ${tw`w-[auto] p-0`}
      }
      & label {
        ${tw`p-0 leading-h1`}
      }
      & .size {
        ${tw`w-[36px] h-[36px] sm:w-[60px] sm:h-[60px] flex justify-center items-center`}
        ${bgColor ? `background-color: ${bgColor};` : null}
      ${textColor ? `color: ${textColor};` : null}
      border: none;

        &:hover,
        &[aria-current="true"] {
          background-color: ${hoverBgColor};
          color: ${hoverTextColor};
        }
      }
    `,
  ],
);
const CategoryWrapper = styledWithInvalidProps("div")(
  ({ contentAlignment }) => [
    tw`flex`,
    css`
      & ul {
        ${tw`pt-0 pb-micro`}
      }
      & .category {
        ${tw`flex flex-wrap items-baseline`}
        ${"left" === contentAlignment ? tw`justify-start` : tw`justify-center`}
        & li {
          ${tw`my-0 mx-pico`}
          & button {
            ${tw`mb-0`}
          }
          & label {
            ${tw`capitalize`}
          }
        }
      }
    `,
  ],
);

const CollectionHeroBannerSizeFilters = withCollectionHeroBannerSizeFilters(
  ({
    active,
    bannerBackgroundColor,
    categoryFilter,
    collectionTitle,
    collectionLinkTitle,
    contentAlignment,
    desktopBanner,
    desktopImage,
    faqHeading,
    handleCategoryClick,
    handleViewAllClick,
    hideCollectionTitle,
    link,
    mobileBanner,
    mobileImage,
    sizeFilter,
    sizeFilterBackgroundColor,
    sizeFilterHoverBackgroundColor,
    sizeFilterHoverTextColor,
    sizeFilterTextColor,
    sizeFilterTitle,
  }) => {
    const { rgb } = bannerBackgroundColor;

    return (
      <div className="md:px-0 flex flex-col items-center px-midi pb-mini pt-maxi sm:py-[40px]">
        {!!desktopImage ? (
          <div className="absolute bottom-zero left-zero right-zero top-zero hidden h-full w-full md:block">
            <CustomImage
              data={desktopImage}
              imageClassName="object-cover object-center h-full"
            />
          </div>
        ) : null}

        {!!mobileImage ? (
          <div className="absolute bottom-zero left-zero right-zero top-zero block h-full w-full md:hidden">
            <CustomImage
              data={mobileImage}
              imageClassName="object-cover object-center h-full"
            />
          </div>
        ) : null}

        {rgb ? (
          <div
            className="absolute top-zero h-full w-full"
            style={{
              backgroundColor: `rgba(${rgb.r},${rgb.g},${rgb.b},${rgb.a})`,
            }}
          />
        ) : null}

        <div
          className={clsx(
            "left" === contentAlignment
              ? "items-start md:pl-midi"
              : "items-center",
            "z-10 flex w-full flex-col",
          )}
        >
          <div className="hidden md:block">
            <RteWrapper contentAlignment={contentAlignment}>
              {desktopBanner}
            </RteWrapper>
          </div>
          <div className="block md:hidden">
            <RteWrapper contentAlignment={contentAlignment}>
              {mobileBanner}
            </RteWrapper>
          </div>

          {!hideCollectionTitle ? (
            <h1 className="my-mini font-body text-h4 font-medium leading-h4">
              {collectionTitle}
            </h1>
          ) : null}

          <p className="font-body text-body font-bold uppercase leading-body">
            {sizeFilterTitle}
          </p>
          <SizeWrapper
            contentAlignment={contentAlignment}
            bgColor={sizeFilterBackgroundColor?.hex}
            textColor={sizeFilterTextColor?.hex}
            hoverBgColor={sizeFilterHoverBackgroundColor?.hex}
            hoverTextColor={sizeFilterHoverTextColor?.hex}
          >
            {sizeFilter && (
              <Filter
                filter={sizeFilter}
                renderFilterList={({ filter, filterListProps }) => (
                  <FilterSectionListSelector
                    className="size"
                    handle={filter.handle}
                    {...filterListProps}
                    hideSizeGuide={true}
                    layout="heroBanner"
                  />
                )}
                showEmptyFilters={true}
              />
            )}
          </SizeWrapper>

          <p className="font-body text-body font-bold uppercase leading-body">
            {collectionLinkTitle}
          </p>
          <CategoryWrapper contentAlignment={contentAlignment}>
            {categoryFilter && (
              <Filter
                filter={categoryFilter}
                renderFilterList={({ filter, filterListProps }) => (
                  <FilterSectionListSelector
                    className="category"
                    handle={filter.handle}
                    hideCheckbox={true}
                    hideProductCount={true}
                    layout="heroBanner"
                    handleViewAllClick={handleViewAllClick}
                    handleCategoryClick={handleCategoryClick}
                    viewAllActive={active}
                    {...filterListProps}
                  />
                )}
                showEmptyFilters={true}
              />
            )}
          </CategoryWrapper>
          {link?.url ? (
            <div className="flex items-baseline" data-testid="faq-link-wrapper">
              <p className="font-body text-hint leading-hint">
                {faqHeading}&nbsp;
              </p>
              <Button
                variant="link"
                href={link.url}
                className="text-white hover:text-white"
              >
                {link.title}
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    );
  },
);

export default CollectionHeroBannerSizeFilters;
